import { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../../context/userContext';
import { DocumentItem } from '../../../types/appModels';
import { useData } from '../../../hooks/useData';
import { DataStatus, StatusUtil } from '../../../types/appTypes';
import { formatCustomerName, decodeSlashes } from '../../../utility/formatCustomerName';
import styles from './DocumentBrowser.module.css';
import Spinner from '../../Common/Spinner/Spinner';
import StatusText from '../../Common/StatusText/StatusText';
import BreadcrumbNav from '../BreadcrumbNav/BreadcrumbNav';
import DocumentHeader from '../DocumentHeader/DocumentHeader';
import UpNavigation from '../UpNavigation/UpNavigation';
import DocumentList from '../DocumentList/DocumentList';
import DownloadModal from '../DownloadModal/DownloadModal';

// Helper function to decode URL-encoded strings
const decodeS3String = (encodedStr: string): string => {
  try {
    return decodeURIComponent(encodedStr);
  } catch (error) {
    console.error('Error decoding S3 string:', error);
    return encodedStr;
  }
};

const DocumentBrowser = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState<string>('');
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadFolderInfo, setDownloadFolderInfo] = useState<{path: string, name: string} | null>(null);
  
  // Get current path from URL and decode it
  const currentPath = location.pathname === '/documentstore' 
    ? '' 
    : location.pathname.replace('/documentstore/', '');
  
  // Calculate the current folder depth to determine download options
  const pathParts = currentPath.split('/').filter(Boolean);
  const folderDepth = pathParts.length;
  
  // Get current folder name and check if it's numeric only
  const currentFolderName = pathParts.length > 0 ? pathParts[pathParts.length - 1] : '';
  const decodedFolderName = decodeURIComponent(currentFolderName);
  const isNumericFolder = /^\d+$/.test(decodedFolderName);
  
  // Format the display name for the folder download button
  // Only format customer names at the root level (when folderDepth is 0)
  const formattedFolderName = isNumericFolder 
    ? `Stage ${decodedFolderName}` 
    : folderDepth === 0 
      ? formatCustomerName(decodedFolderName)
      : decodedFolderName;

  // First replace %2F with REPLACINGSLASHES, then encode, then convert back to %252F
  const encodedApiPath = currentPath
    .replace(/%2F/g, 'REPLACINGSLASHES')
  const decodedPath = decodeURIComponent(encodedApiPath);
  const tempPath = encodeURIComponent(decodedPath);
  const finalEncodedApiPath = tempPath.replace(/REPLACINGSLASHES/g, '%252F');

  const folderEndpoint = `/Documents/Folder${
    encodedApiPath ? `?path=${finalEncodedApiPath}` : ''
  }`;

  const { dataState, reloadData } = useData<DocumentItem[]>({
    endpoint: folderEndpoint,
    initialData: [],
    initialStatus: DataStatus.Loading,
    loadWhen: true,
    modifyData: (data) => {
      if (!data) return data;
      
      // Only format names at root level (when currentPath is empty)
      if (!currentPath) {
        return data.map(item => ({
          ...item,
          name: formatCustomerName(decodeSlashes(decodeURIComponent(item.name)))
        }));
      }
      return data;
    }
  });

  const changePath = (newPath: string) => {
    // Update URL with new path - encode it properly to preserve %2F
    // Remove trailing slash for root path
    const urlPath = newPath === '' ? '' : `/${newPath}`;
    
    // Encode the path but preserve %2F by first replacing it, then encoding, then restoring
    const tempPath = urlPath.replace(/%2F/g, '__TEMP_SLASH__');
    const encodedPath = encodeURI(tempPath).replace(/__TEMP_SLASH__/g, '%2F');
    const finalEncodedPath = encodedPath.replace(/#/g, '%23');
    
    navigate(`/documentstore${finalEncodedPath}`);
    setSelectedFiles(new Set());
    setFilterText('');
    setTimeout(() => reloadData(), 10);
  };

  const handleFileSelect = (path: string) => {
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleItemClick = (item: DocumentItem) => {
    if (item.type === 'Folder') {
      // Always navigate into folders on click
      changePath(item.path);
    } else {
      // Handle file selection
      handleFileSelect(item.path);
    }
  };

  const handleFileDownload = async (path: string) => {
    try {
      const pathWithPreservedSlashes = path.replace(/%2F/g, 'REPLACINGSLASHES');
      const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
      const apiPath = decodedPath.replace(/REPLACINGSLASHES/g, '%2F');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Documents/File?path=${encodeURIComponent(apiPath)}`,
        {
          headers: {
            Authorization: `Bearer ${user!.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get the filename from the path and decode it for display
      const encodedFilename = path.split('/').pop() || 'download';
      a.download = decodeURIComponent(encodedFilename);
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleMultipleDownload = async () => {
    const filesToDownload = Array.from(selectedFiles);
    
    if (filesToDownload.length === 1) {
      await handleFileDownload(filesToDownload[0]);
      return;
    }
    
    try {
      const apiFilePaths = filesToDownload.map(path => {
        const pathWithPreservedSlashes = path.replace(/%2F/g, 'REPLACINGSLASHES');
        const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
        return decodedPath.replace(/REPLACINGSLASHES/g, '%252F');
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Documents/Files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user!.accessToken}`,
          },
          body: JSON.stringify(apiFilePaths),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download files');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Create a more descriptive filename that includes the folder hierarchy
      let zipFileName = 'documents.zip';
      
      if (currentPath) {
        // Split the path by '/' to get all folder names
        const pathParts = currentPath.split('/').filter(Boolean);
        
        if (pathParts.length > 0) {
          // Decode and format each part
          const formattedParts = pathParts.map((part, index) => {
            const decodedPart = decodeURIComponent(part);
            
            // Format the first part (customer name) using formatCustomerName
            if (index === 0) {
              return formatCustomerName(decodedPart);
            }
            
            // Format numeric parts as stages
            if (/^\d+$/.test(decodedPart)) {
              return `Stage ${decodedPart}`;
            }
            
            return decodedPart;
          });
          
          // Join all parts with dashes to create the filename
          zipFileName = `${formattedParts.join(' - ')}.zip`;
        }
      }
          
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading multiple files:', error);
    }
  };

  const startFolderDownload = () => {
    // Store the current folder path and formatted name for download
    setDownloadFolderInfo({
      path: currentPath,
      name: formattedFolderName
    });
    
    // Show the download modal
    setShowDownloadModal(true);
  };

  const handleConfirmFolderDownload = async (groupBy: 'Stage' | 'FileType' | 'None') => {
    // Set downloading flag to true
    setIsDownloading(true);
    
    // Perform the actual download using the stored folder path
    if (downloadFolderInfo) {
      await performFolderDownload(downloadFolderInfo.path, groupBy);
    }
    
    // Reset states after download completes
    setIsDownloading(false);
    setShowDownloadModal(false);
    setDownloadFolderInfo(null);
  };

  const handleCancelFolderDownload = () => {
    setShowDownloadModal(false);
    setIsDownloading(false);
    setDownloadFolderInfo(null);
  };

  const performFolderDownload = async (folderPathToDownload: string, groupBy: 'Stage' | 'FileType' | 'None') => {
    try {
      const pathWithPreservedSlashes = folderPathToDownload.replace(/%2F/g, 'REPLACINGSLASHES');
      const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
      const formattedFolderPath = decodedPath.endsWith('/') 
        ? decodedPath 
        : `${decodedPath}/`;
      
      const apiFolderPath = formattedFolderPath.replace(/REPLACINGSLASHES/g, '%2F');
  
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Documents/Folder/Files?recursive=true&groupBy=${groupBy}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user!.accessToken}`,
          },
          body: JSON.stringify([apiFolderPath]),
        }
      );
  
      if (!response.ok) {
        // Handle the specific 'no-files' error
        if (response.status === 400) {
          const errorData = await response.json();
          console.log('Folder Download - Error response:', errorData);
          if (errorData.code === 'no-files') {
            alert('This folder does not contain any files to download.');
            return;
          }
        }
        throw new Error('Failed to download folder');
      }
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate a meaningful filename based on the downloaded folder
      let zipFileName = 'folder.zip';
      
      // Use the decoded path for filename generation
      const downloadPathParts = decodedPath.split('/').filter(Boolean);
      
      if (downloadPathParts.length > 0) {
        // Decode and format each part
        const formattedParts = downloadPathParts.map((part, index) => {
          const decodedPart = decodeURIComponent(part);
          
          // Format the first part (customer name)
          if (index === 0) {
            return formatCustomerName(decodedPart);
          }
          
          // Format numeric folders as "Stage X"
          if (/^\d+$/.test(decodedPart)) {
            return `Stage ${decodedPart}`;
          }
          
          return decodedPart;
        });
        
        // Join all parts with dashes to create the filename
        zipFileName = `${formattedParts.join(' - ')}.zip`;
      }
      
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading folder:', error);
    }
  };

  const navigateUp = () => {
    if (!currentPath) return;
    
    // First replace %2F with REPLACINGSLASHES to preserve them
    const pathWithPreservedSlashes = currentPath.replace(/%2F/g, 'REPLACINGSLASHES');
    
    // Decode the path to work with it
    const decodedPath = decodeURIComponent(pathWithPreservedSlashes);
    
    // Remove trailing slash if present
    const pathToProcess = decodedPath.endsWith('/') ? 
      decodedPath.slice(0, -1) : decodedPath;
    
    // Split and remove the last part
    const pathParts = pathToProcess.split('/');
    pathParts.pop();
    
    // Join back and ensure proper encoding
    const newPath = pathParts.join('/');
    
    // Ensure the path ends with %2F for API calls
    const finalPath = newPath ? `${newPath}/` : '';
    
    setFilterText('');
    changePath(finalPath);
  };

  const handleUploadComplete = () => {
    reloadData();
  };

  const handleDeleteComplete = () => {
    setSelectedFiles(new Set());
    reloadData();
  };

  return (
    <div className={styles.container}>
      {showDownloadModal && downloadFolderInfo && (
        <DownloadModal
          folderName={downloadFolderInfo.name}
          initialFolderName={downloadFolderInfo.name}
          onConfirm={handleConfirmFolderDownload}
          onCancel={handleCancelFolderDownload}
          isDownloading={isDownloading}
        />
      )}

      <div className={styles.headerSection}>
        <DocumentHeader 
          currentPath={currentPath}
          filterText={filterText}
          setFilterText={setFilterText}
          selectedFiles={selectedFiles}
          onMultipleDownload={handleMultipleDownload}
          showFolderDownload={folderDepth >= 2}
          currentFolderName={formattedFolderName}
          onDownloadEntireFolder={startFolderDownload}
          onUploadComplete={handleUploadComplete}
          onDeleteComplete={handleDeleteComplete}
        >
          <BreadcrumbNav 
            currentPath={currentPath}
            onPathChange={changePath}
            maxVisibleFolders={2}
          />
        </DocumentHeader>

        {currentPath && (
          <UpNavigation onNavigateUp={navigateUp} />
        )}
      </div>

      <div className={styles.contentSection}>
        {StatusUtil.isIdleOrLoading(dataState.status) ? (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        ) : StatusUtil.isFail(dataState.status) ? (
          <div className={styles.statusContainer}>
            <StatusText
              text='An error occurred and the documents could not be loaded.'
              error={true}
            />
          </div>
        ) : (
          <DocumentList
            items={dataState.data?.filter(item => {
              if (!filterText) return true;
              // Decode the name before filtering
              const decodedName = decodeS3String(item.name);
              return decodedName.toLowerCase().includes(filterText.toLowerCase());
            }) || []}
            currentPath={currentPath}
            selectedFiles={selectedFiles}
            onItemClick={handleItemClick}
            onFileDownload={handleFileDownload}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentBrowser;